import { devtools } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import baseConfig from '@/config';
import { isCnArea } from '@/utils/envHelper';

// 存储持久化的key
const key = 'f_avatar_video_pro_state';

export type AvatarInfoType = {
  url: string;
  previewUrl: string;
  title: string;
  assetsId?: number;
};

export enum ScriptType {
  TTS = 0,
  AUDIO = 1,
}

// 音色信息类型
export type VoiceInfoType = {
  icon: string;
  tags?: string[];
  previewUrl?: string;
  title: string;
  templateId?: string | number;
  id?: string | number;
  voiceId: string;
  gender: number;
  isVcm: boolean;
  isPro?: boolean;
  [propname: string]: any;
};

// 上传音频信息类型
export type UploadVoiceInfo = {
  url: string;
  name: string;
};

// Avatar Video Pro Store
interface IAvatarVideoProStore {
  assetsInfo?: Record<string, any>;
  setAssetsInfo: (assetsInfo?: Record<string, any>) => void;
  avatarInfo: AvatarInfoType | null;
  setAvatarInfo: (avatarInfo: AvatarInfoType | null) => void;
  // tts音频
  voiceUrl: string;
  setVoiceUrl: (url: string) => void;
  ttsSpeed: number;
  setTtsSpeed: (speed: number) => void;
  uploadVoiceInfo: UploadVoiceInfo | null;
  setUploadVoiceInfo: (info: UploadVoiceInfo | null) => void;
  ttsDuration: number;
  setTtsDuration: (ttsDuration: number) => void;
  duration: number;
  setDuration: (time: number) => void;
  scriptType: ScriptType;
  setScriptType: (scriptType: ScriptType) => void;
  script: string;
  setScript: (script: string) => void;
  voiceInfo: VoiceInfoType | null;
  setVoiceInfo: (info: VoiceInfoType | null) => void;
  ttsGenerating: boolean;
  setTtsGenerating: (generating: boolean) => void;
  resetData: () => void;

  // 字体模板
  fontTemplateInfo: {
    id: string;
    isPro: boolean;
  };
  setFontTemplateInfo: (value: { id: string; isPro: boolean }) => void;

  extraEventResult: string;
  setExtraEventResult: (value: string) => void;
}

const initData = {
  // 选择的数字人信息
  avatarInfo: null,
  // 生成的TTS音频链接
  voiceUrl: '',
  // TTS音频速度
  ttsSpeed: 1,
  // 上传的音频文件信息
  uploadVoiceInfo: null,
  // 生成的TTS音频时长
  ttsDuration: 0,
  // 上传的音频时长
  duration: 0,
  // 脚本面板类型：0-TTS，1-音频文件
  scriptType: ScriptType.TTS,
  // 脚本内容
  script: '',
  ttsGenerating: false,
  // 选择的音色信息
  voiceInfo: {
    icon: '/images/voice/default_man_icon.png',
    previewUrl: 'https://assets.falcocut.ai/template/default_voiceInfo_man.mp3',
    title: isCnArea ? 'Christopher-美式英语' : 'Christopher-AmE',
    isPro: false,
    tags: isCnArea ? ['男', '年轻', '平静的'] : ['Male', 'Young Adult', 'Calm'],
    voiceId: 'Liam',
    tabGroupName: 'default',
    gender: 1,
    isVcm: true,
  },
  fontTemplateInfo: {
    id: '',
    isPro: false,
  },

  extraEventResult: '',
};

export const useAvatarProStore = createWithEqualityFn<IAvatarVideoProStore>()(
  devtools(
    (set) => ({
      ...initData,
      setAssetsInfo(assetsInfo) {
        set({ assetsInfo });
      },
      setAvatarInfo(avatarInfo) {
        set({ avatarInfo });
      },

      setVoiceUrl(voiceUrl) {
        set({ voiceUrl });
      },

      setTtsSpeed(speed: number) {
        set({ ttsSpeed: speed });
      },

      setUploadVoiceInfo(uploadVoiceInfo) {
        set({ uploadVoiceInfo });
      },

      setTtsDuration: (ttsDuration: number) => {
        set({ ttsDuration });
      },

      setDuration(duration) {
        set({ duration });
      },

      setScriptType(scriptType) {
        set({ scriptType });
      },

      setScript(script) {
        set({ script });
      },

      setVoiceInfo(voiceInfo) {
        set({ voiceInfo });
      },

      setTtsGenerating(generating: boolean) {
        set({ ttsGenerating: generating });
      },

      resetData: () => {
        set({
          ...initData,
        });
      },

      setFontTemplateInfo: (value) => {
        set({ fontTemplateInfo: value });
      },

      setExtraEventResult: (value: string) => {
        set({ extraEventResult: value });
      },
    }),
    {
      name: key,
      enabled: baseConfig.debuggable,
    },
  ),
  shallow,
);
